import React, { useState, useEffect } from "react";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Button from "@mui/material/Button";
import {
  DataGrid,
  GridToolbarContainer,
  GridToolbarColumnsButton,
  GridToolbarDensitySelector,
} from "@mui/x-data-grid";
import { useNavigate } from "react-router-dom";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import { useDispatch, useSelector } from "react-redux";
import { setSelectedRecords } from "../../pages/DisplayRecordsPage/displayRecordsSlice";
import DownloadIcon from "@mui/icons-material/Download";
import FileUploadIcon from "@mui/icons-material/FileUpload";
import RefreshIcon from "@mui/icons-material/Refresh";
import Typography from "@mui/material/Typography";
import { setHeaderMapping } from "../../state/Match/matchSlice";
import { resetUploadStatus } from "../../state/FileUpload/fileUploadSlice";
import { getJobDetails } from "../../state/FileUpload/fileUploadSlice";
import { useParams } from "react-router-dom";

const exportToCSV = (rows, columns, filename) => {
  const selectedColumns = columns.filter(
    (column) =>
      column.field === "firstName" ||
      column.field === "lastName" ||
      column.field === "fullName" ||
      column.field === "imcId" ||
      column.field === "oneKeyId" ||
      column.field === "onekeyValidationRequired" ||
      column.field === "automatchRequired" ||
      column.field === "manualmatchRequired" ||
      column.field === "wpName" ||
      column.field === "address" ||
      column.field === "city" ||
      column.field === "state" ||
      column.field === "postCode" ||
      column.field === "specialty" ||
      column.field === "fax" ||
      column.field === "phone" ||
      column.field === "jobId" ||
      column.field === "email" ||
      column.field === "address1" ||
      column.field === "address2" ||
      column.field === "title" ||
      column.field === "status" ||
      column.field === "onekeyStatus"
  );

  const csvRows = [];
  const headers = selectedColumns.map((col) => `"${col.headerName}"`);
  csvRows.push(headers.join(","));

  rows?.forEach((row) => {

    const values = selectedColumns.map((col) => {
      let value = row[col.field];
      
      if (typeof value === "string") {
        value = value.replace(/"/g, '""'); 
        return `"${value}"`; 
      }
      
      return value;
    });
    
    csvRows.push(values.join(","));
  });

  const csvString = csvRows.join("\n");
  const blob = new Blob([csvString], { type: "text/csv" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.setAttribute("hidden", "");
  a.setAttribute("href", url);
  a.setAttribute("download", `${filename}.csv`);
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
};

function CustomToolbar({
  rows,
  columns,
  handleClickOpen,
  handleGetOneKeyStatus,
  handleRefreshRecords,
  completeJob,
  validateAddress,
  sendAllJobs,
}) {
  const [open, setOpen] = useState(false);
  const [filename, setFilename] = useState("");
  const { token } = useSelector((state) => state.login);
  const { id } = useParams();

  const handleExportClick = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleConfirm = () => {
    if (filename) {
      exportToCSV(rows, columns, filename);
      setOpen(false);
    }
  };

  const navigate = useNavigate();
  const dispatch = useDispatch();

  const navigateToFileImport = () => {
    dispatch(setHeaderMapping([]));
    dispatch(resetUploadStatus());
    dispatch(getJobDetails({ jobId: id, token: token }));
    navigate("/custome-rmatch-portal/fileUpload", { state: { isDropdownDisabled: true } });
  };

  return (
    <GridToolbarContainer>
      <Stack
        spacing={2}
        direction="row"
        sx={{ justifyContent: "space-between", width: "100%" }}
      >
        <Box sx={{ display: "flex", gap: 2 }}>
          <GridToolbarColumnsButton style={{ color: "#ab1368" }} />
          {/* <GridToolbarDensitySelector/>
          <Tooltip title="Send selected records to Onekey">
            <Button onClick={handleClickOpen}>AUTO-MATCH</Button>
          </Tooltip>
          <Tooltip title="Request Validation Status from Onekey">
            <Button variant="text" onClick={handleGetOneKeyStatus}>
              MANUAL-MATCH
            </Button>
          </Tooltip> */}
          <Tooltip title="Navigate to re-upload exported file">
            <Button
              onClick={navigateToFileImport}
              startIcon={<FileUploadIcon />}
            >
              IMPORT
            </Button>
          </Tooltip>
          <Tooltip title="Export the data to CSV for adjustments and re-upload">
            <Button
              onClick={handleExportClick}
              startIcon={<DownloadIcon />}
              sx={{ color: "#1976d2" }}
            >
              EXPORT
            </Button>
          </Tooltip>
          <Tooltip title="Mark the job as completed">
            <Button variant="text" onClick={completeJob}>
              COMPLETE JOB
            </Button>
          </Tooltip>
          <Tooltip title="Mark the job as completed">
            <Button variant="text" onClick={validateAddress}>
              Validate Address
            </Button>
          </Tooltip>
        </Box>

        <Box sx={{ marginLeft: "auto" }}>
          <Tooltip title="Refresh all records">
            <Button
              variant="text"
              onClick={handleRefreshRecords}
              startIcon={<RefreshIcon sx={{ color: "#1976d2" }} />}
            >
              REFRESH
            </Button>
          </Tooltip>
        </Box>
      </Stack>
      <Dialog open={open} onClose={handleClose}>
        <DialogTitle>Export CSV</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Please enter a filename for the exported CSV.
          </DialogContentText>
          <TextField
            autoFocus
            margin="dense"
            label="Filename"
            type="text"
            fullWidth
            variant="standard"
            value={filename}
            onChange={(e) => setFilename(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button onClick={handleConfirm}>Export</Button>
        </DialogActions>
      </Dialog>
    </GridToolbarContainer>
  );
}

const RecordDisplayGrid = ({
  rows,
  columns,
  handleClickOpen,
  handleGetOneKeyStatus,
  handleRefreshRecords,
  completeJob,
  validateAddress,
  sendAllJobs,
  columnVisibilityModel,
  setColumnVisibilityModel,
}) => {
  const dispatch = useDispatch();
  const [rowSelectionModel, setRowSelectionModel] = useState([]);
  const [matchedCount, setMatchedCount] = useState(0);
  const [locallyMatchedCount, setLocallyMatchedCount] = useState(0);
  const [oneKeyMatchedCount, setOneKeyMatchedCount] = useState(0);
  const [manualMatchedCount, setManualMatchedCount] = useState(0);
  const [validationPendingCount, setValidationPendingCount] = useState(0);
  const [unmatchedCount, setUnmatchedCount] = useState(0);

  useEffect(() => {
    const matched = rows.filter(
      (row) => row.oneKeyId && row.oneKeyId.trim() !== ""
    ).length;
    // const oneKeyMatched = rows.filter(
    //   (row) => row.oneKeyId && row.onekeyRequestId
    // ).length;
    const oneKeyMatched = rows.filter(
      (row) => row.status === "AutoMatched"
    ).length;
    const manualMatched = rows.filter(
      (row) => row.status === "ManualMatched"
    ).length;
    // const locallyMatched = matched - oneKeyMatched;
    const locallyMatched = rows.filter(
      (row) => row.status === "LocalMatched"
    ).length;
    const validationPending = rows.filter(
      (row) => row.onekeyStatus === "PENDING"
    ).length;
    // const unmatched = rows.filter(
    //   (row) =>
    //     row.onekeyMatched === "False" &&
    //     row.onekeyStatus === "NOTFOUND"
    // ).length;
    const unmatched = rows.filter(
      (row) => row.onekeyMatched === "False"
    ).length;

    setMatchedCount(matched);
    setLocallyMatchedCount(locallyMatched);
    setOneKeyMatchedCount(oneKeyMatched);
    setManualMatchedCount(manualMatched);
    setValidationPendingCount(validationPending);
    setUnmatchedCount(unmatched);
  }, [rows]);

  useEffect(() => {
    dispatch(setSelectedRecords(rowSelectionModel));
  }, [rowSelectionModel, dispatch]);

  return (
    <>
      <Box sx={{ height: "500px", width: "96%", marginTop: "20px" }}>
        <Box
          sx={{
            padding: "10px",
            backgroundColor: "#f0f0f0",
            borderRadius: "5px",
            marginBottom: "5px",
          }}
        >
          <Typography
            variant="h7"
            sx={{
              fontWeight: "bold",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Tooltip title="Records matched after local matching">
              <span>Local Matched : {locallyMatchedCount}</span>
            </Tooltip>
            <Tooltip title="Records matched after being sent to Onekey">
              <span>Auto Matched : {oneKeyMatchedCount}</span>
            </Tooltip>
            <Tooltip title="Records matched after Manual Matching">
              <span>Manual Matched : {manualMatchedCount}</span>
            </Tooltip>
            <Tooltip title="Records with Onekey Validation Status as 'pending'">
              <span>Validation Pending : {validationPendingCount}</span>
            </Tooltip>
            <Tooltip title="Unmatched Records">
              <span>Unmatched : {unmatchedCount}</span>
            </Tooltip>
          </Typography>
        </Box>
        <DataGrid
          slots={{
            toolbar: CustomToolbar,
          }}
          slotProps={{
            toolbar: {
              rows,
              columns,
              handleClickOpen,
              handleGetOneKeyStatus,
              handleRefreshRecords,
              completeJob,
              validateAddress,
              sendAllJobs,
              matchedCount,
              totalCount: rows.length,
              locallyMatchedCount,
              oneKeyMatchedCount,
              validationPendingCount,
            },
          }}
          rowHeight={50}
          rows={rows}
          columns={columns}
          sx={{
            "& .MuiDataGrid-row": {
              backgroundColor: "dadfe1",
            },
            "& .MuiDataGrid-columnHeader": {
              backgroundColor: "#dee3eb",
              color: "#ab1368",
              fontSize: 16,
            },
            overflowX: "scroll",
            border: "2px solid #ab1368",
            borderRadius: "5px",
          }}
          initialState={{
            density: "compact",
            columns: {
              columnVisibilityModel: columnVisibilityModel,
            },
            pagination: {
              paginationModel: {
                pageSize: 25,
              },
            },
          }}
          pageSizeOptions={[25, 50, 75, 100]}
          disableRowSelectionOnClick
          checkboxSelection
          onRowSelectionModelChange={(newRowSelectionModel) => {
            setRowSelectionModel(newRowSelectionModel);
          }}
          rowSelectionModel={rowSelectionModel}
          columnVisibilityModel={columnVisibilityModel}
          onColumnVisibilityModelChange={(newModel) =>
            setColumnVisibilityModel(newModel)
          }
        />
      </Box>
    </>
  );
};

export default RecordDisplayGrid;
